import type { StrapiQuotationRow, StrapiQuotationUserStub } from '../../api/strapiCrm';
import type { StrapiUser } from '../../api/strapi';
import { quotationRequiresManajerApproval } from './quotationGrandTotal';

export type QuotationWorkflowGrandTotalOpts = {
  /** Grand total dari detail quotation (wajib untuk gate ≥ Rp 20 juta di grid/list). */
  grandTotal?: number | null;
};

export function quotationRowNeedsWorkflowFinancialDetail(status?: string | null): boolean {
  const s = status ?? '';
  return (
    s === 'submitted' ||
    s === 'approved' ||
    s === 'need_manajer_approval' ||
    s === 'manajer_approved'
  );
}

/** Workflow status codes on quotation (selaras master `quotation-status`). */
export type QuotationWorkflowStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'need_manajer_approval'
  | 'manajer_approved'
  | 'rejected'
  | 'rejected_by_manajer'
  | 'customer_approved'
  | 'need_attention'
  | 'edit_requested';

export const QUOTATION_WORKFLOW_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Diajukan',
  approved: 'Disetujui SPV',
  need_manajer_approval: 'Menunggu persetujuan manajer',
  manajer_approved: 'Disetujui manajer',
  rejected: 'Ditolak',
  rejected_by_manajer: 'Ditolak manajer',
  customer_approved: 'Disetujui pelanggan',
  need_attention: 'Need Attention',
  edit_requested: 'Permintaan revisi',
  sent: 'Terkirim',
  customer_detail: 'Detail pelanggan',
  in_progress: 'Berjalan',
  completed: 'Selesai',
  cancelled: 'Dibatalkan',
  paid: 'Dibayar',
};

export function quotationStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return QUOTATION_WORKFLOW_STATUS_LABEL[status] ?? status;
}

/** Status paralel permintaan revisi (tidak menimpa `status` utama). */
export type QuotationEditRequestStatus = 'none' | 'pending' | 'approved' | 'rejected';

export const QUOTATION_EDIT_REQUEST_STATUS_LABEL: Record<QuotationEditRequestStatus, string> = {
  none: 'Tidak ada',
  pending: 'Menunggu review SPV',
  approved: 'Disetujui SPV',
  rejected: 'Ditolak SPV',
};

/**
 * Baca status permintaan revisi.
 * Legacy: `progress_status === 'edit_requested'` dianggap sudah disetujui (PIC boleh edit).
 */
export function quotationEditRequestStatus(
  row: Pick<StrapiQuotationRow, 'progress_status' | 'edit_request_status'>,
): QuotationEditRequestStatus {
  const raw = String(row.edit_request_status ?? 'none').trim();
  if (raw === 'pending' || raw === 'approved' || raw === 'rejected') return raw;
  if (quotationWorkflowStatus(row) === 'edit_requested') return 'approved';
  return 'none';
}

export function quotationHasApprovedEditRequest(
  row: Pick<StrapiQuotationRow, 'progress_status' | 'edit_request_status'>,
): boolean {
  return quotationEditRequestStatus(row) === 'approved';
}

/** Petugas sampling mengubah quotation di lapangan (tambah sampel / titik). */
export function quotationIsNeedAttention(row: { progress_status?: string | null }): boolean {
  return quotationWorkflowStatus(row) === 'need_attention';
}

/** PIC boleh edit / ajukan ulang karena revisi lapangan atau permintaan revisi disetujui. */
export function quotationAllowsPicRevisionEdit(
  row: Pick<StrapiQuotationRow, 'progress_status' | 'edit_request_status'>,
): boolean {
  const s = quotationWorkflowStatus(row);
  return s === 'edit_requested' || s === 'need_attention' || quotationHasApprovedEditRequest(row);
}

/** Label status: jika ada permintaan revisi aktif, tampilkan status revisi saja. */
export function quotationStatusDisplayLabel(
  row: Pick<StrapiQuotationRow, 'progress_status' | 'edit_request_status'>,
): string {
  if (quotationIsNeedAttention(row)) return QUOTATION_WORKFLOW_STATUS_LABEL.need_attention;
  const ers = quotationEditRequestStatus(row);
  if (ers === 'pending') return 'Revisi menunggu SPV';
  if (ers === 'approved' && quotationWorkflowStatus(row) !== 'edit_requested') {
    return 'Revisi disetujui';
  }
  return quotationStatusLabel(row.progress_status);
}

export function isMarketingPic(user: StrapiUser | null | undefined): boolean {
  if (!user) return false;
  const code = user.userTypeCode?.trim().toLowerCase();
  if (code === 'marketing_pic') return true;
  const name = user.userTypeName?.trim().toLowerCase() ?? '';
  return name.includes('marketing') && name.includes('pic');
}

export function isAdminKeuangan(user: StrapiUser | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'admin_keuangan';
}

/** Hanya PIC marketing yang membuat quotation baru (bukan SPV/manajer/keuangan). */
export function canCreateQuotation(user: StrapiUser | null | undefined): boolean {
  if (!user) return false;
  if (isAdminKeuangan(user)) return false;
  if (isMarketingManajer(user) || isMarketingSpv(user)) return false;
  return isMarketingPic(user) || !isMarketingPic(user);
}

export function isMarketingSpv(user: StrapiUser | null | undefined): boolean {
  if (!user) return false;
  const code = user.userTypeCode?.trim().toLowerCase();
  if (code === 'marketing_spv' || code === 'spv_marketing') return true;
  const name = user.userTypeName?.trim().toLowerCase() ?? '';
  return name.includes('marketing') && name.includes('spv');
}

export function isMarketingManajer(user: StrapiUser | null | undefined): boolean {
  if (!user) return false;
  const code = user.userTypeCode?.trim().toLowerCase();
  if (code === 'marketing_manajer') return true;
  const name = user.userTypeName?.trim().toLowerCase() ?? '';
  return name.includes('marketing') && name.includes('manajer');
}

export function isSpvAdminLhu(user: StrapiUser | null | undefined): boolean {
  const code = user?.userTypeCode?.trim().toLowerCase();
  return code === 'spv_admin_lhu' || code === 'admin_lhu';
}

/** @deprecated Prefer {@link isSpvAdminLhu}. */
export function isAdminLhu(user: StrapiUser | null | undefined): boolean {
  return isSpvAdminLhu(user);
}

export function isAdminWorkOrderPic(user: StrapiUser | null | undefined): boolean {
  const code = user?.userTypeCode?.trim().toLowerCase();
  return code === 'admin_workorder' || code === 'admin_workorder_pic';
}

export function isAdminSampling(user: StrapiUser | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'admin_sampling';
}

export function isPetugasSampling(user: StrapiUser | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'petugas_sampling';
}

export function quotationUsesSampleGroups(row: {
  is_contract?: boolean | null;
  is_consultant_quote?: boolean | null;
}): boolean {
  return Boolean(row.is_contract || row.is_consultant_quote);
}

/** Konsultan murni: satu pelanggan per quotation group, bukan kontrak. */
export function isConsultantOnlyQuotation(row: {
  is_contract?: boolean | null;
  is_consultant_quote?: boolean | null;
}): boolean {
  return Boolean(row.is_consultant_quote) && !row.is_contract;
}

const LHU_ISSUED_STATUSES = new Set(['cetak', 'lhu_rilis']);

/** Semua pelanggan konsultan sudah punya work order yang LHU-nya terbit. */
export function consultantQuotationLhuFullyIssued(row: StrapiQuotationRow): boolean {
  if (!isConsultantOnlyQuotation(row)) return false;
  const fromGroups = (row.quotation_groups ?? [])
    .map((group) => group.documentId?.trim() ?? '')
    .filter(Boolean);
  const groupIds = fromGroups.length
    ? fromGroups
    : [
        ...new Set(
          (row.sampling_plans ?? [])
            .map((plan) => plan.quotation_group?.documentId?.trim() ?? '')
            .filter(Boolean),
        ),
      ];
  if (!groupIds.length) return false;
  const plans = row.sampling_plans ?? [];
  return groupIds.every((groupId) => {
    const statuses = new Map<string, string>();
    for (const plan of plans) {
      if (plan.quotation_group?.documentId?.trim() !== groupId) continue;
      const workOrderId = plan.work_order?.documentId?.trim() ?? '';
      if (!workOrderId) continue;
      statuses.set(
        workOrderId,
        String(plan.work_order?.laboratory_testing_status ?? '').trim().toLowerCase(),
      );
    }
    if (statuses.size === 0) return false;
    for (const status of statuses.values()) {
      if (!LHU_ISSUED_STATUSES.has(status)) return false;
    }
    return true;
  });
}

/** Label tipe quotation untuk grid: flat | kontrak | konsultan | kontrak & konsultan. */
export function resolveQuotationTypeLabel(row: {
  is_contract?: boolean | null;
  is_consultant_quote?: boolean | null;
}): string {
  const contract = Boolean(row.is_contract);
  const consultant = Boolean(row.is_consultant_quote);
  if (contract && consultant) return 'Kontrak & konsultan';
  if (contract) return 'Kontrak';
  if (consultant) return 'Konsultan';
  return 'Flat';
}

/** Superadmin: permission preset includes `can_view_user` (menu Users / impersonate). */
export function isSuperadminUser(accessCodes: readonly string[] | undefined | null): boolean {
  return accessCodes?.includes('can_view_user') ?? false;
}

function emailsMatch(a?: string | null, b?: string | null): boolean {
  const x = a?.trim().toLowerCase();
  const y = b?.trim().toLowerCase();
  return Boolean(x && y && x === y);
}

/** Gabungkan session user dengan baris Users API (documentId / numeric id lebih lengkap). */
export function resolveQuotationWorkflowUser(
  user: StrapiUser | null | undefined,
  managedRows?: Array<{
    documentId?: string | null;
    numericId?: number | null;
    email?: string | null;
  }> | null,
): StrapiUser | null | undefined {
  if (!user?.email?.trim()) return user;
  const email = user.email.trim().toLowerCase();
  const managed = managedRows?.find((u) => u.email?.trim().toLowerCase() === email);
  if (!managed) return user;
  return {
    ...user,
    documentId: user.documentId?.trim() || managed.documentId?.trim() || null,
    id: user.id || managed.numericId || user.id,
  };
}

export function quotationWorkflowStatus(row: { progress_status?: string | null }): string {
  return String(row.progress_status ?? 'draft').trim() || 'draft';
}

/** Quotation dikembalikan ke PIC (ditolak SPV atau manajer). */
export function quotationIsReturnedToPicStatus(status?: string | null): boolean {
  const s = String(status ?? '').trim();
  return s === 'rejected' || s === 'rejected_by_manajer';
}

export function isQuotationAssignee(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (!user) return false;
  const assignee: StrapiQuotationUserStub | null | undefined = row.assignee_user;
  if (!assignee) return false;

  const userDocId = user.documentId?.trim();
  const assigneeDocId = assignee.documentId?.trim();
  if (userDocId && assigneeDocId && userDocId === assigneeDocId) return true;
  if (assignee.id != null && user.id === assignee.id) return true;
  if (userDocId && assignee.id != null && userDocId === String(assignee.id)) return true;
  if (emailsMatch(assignee.email, user.email)) return true;
  return Boolean(
    assignee.username?.trim() &&
      user.username?.trim() &&
      assignee.username.trim().toLowerCase() === user.username.trim().toLowerCase(),
  );
}

/** SPV yang ditugaskan pada quotation (`approved_by_user`). */
export function isQuotationApprover(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (!user || !isMarketingSpv(user)) return false;
  const approver: StrapiQuotationUserStub | null | undefined = row.approved_by_user;
  if (!approver?.email && !approver?.username) return false;
  if (emailsMatch(approver.email, user.email)) return true;
  return Boolean(
    approver.username?.trim() &&
      user.username?.trim() &&
      approver.username.trim() === user.username.trim(),
  );
}

/** Aksi SPV workflow: semua user tipe SVP marketing (tidak harus sama dengan `approved_by_user`). */
function canUseSpvWorkflowActions(
  _row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (isAdminLhu(user)) return true;
  return isMarketingSpv(user);
}

/**
 * Quotation dikunci penuh (tanpa edit / minta revisi).
 * TODO: aktifkan saat work order terkait mencapai status tertentu (mis. submitted/approved).
 */
export function quotationIsLocked(_row: StrapiQuotationRow): boolean {
  return false;
}

/** @deprecated gunakan `quotationIsLocked` — saat ini WO terbuat belum mengunci quotation. */
export function quotationIsViewOnly(row: StrapiQuotationRow): boolean {
  return quotationIsLocked(row);
}

export function quotationHasWorkOrder(row: StrapiQuotationRow): boolean {
  return Boolean(row.work_order_created);
}

/** Boleh mengubah quotation (form / grid). */
export function canPicEditQuotation(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsViewOnly(row)) return false;
  // Admin keuangan: view-only quotation (no edit/delete).
  if (isAdminKeuangan(user)) return false;
  // SPV / manajer: selalu bisa edit (kasus darurat / backup saat PIC/SPV cuti).
  if (isMarketingSpv(user) || isMarketingManajer(user)) return true;

  const s = quotationWorkflowStatus(row);
  const assigneeMatch = isQuotationAssignee(row, user);
  const marketingPic = isMarketingPic(user);

  if (isAdminLhu(user)) {
    return s === 'draft' || quotationAllowsPicRevisionEdit(row);
  }
  if (s === 'draft') {
    if (marketingPic || assigneeMatch) return true;
    return !marketingPic && !isMarketingSpv(user);
  }
  if (quotationAllowsPicRevisionEdit(row)) {
    return marketingPic || assigneeMatch;
  }
  if (quotationIsReturnedToPicStatus(s)) {
    return marketingPic || assigneeMatch;
  }
  return false;
}

export function canShowCreateDraftSubmitButtons(isCreate: boolean): boolean {
  return isCreate;
}

/** Tombol Ajukan pada form edit (draft / ditolak / permintaan revisi disetujui). */
export function canShowSubmitOnQuotationEdit(
  row: StrapiQuotationRow | null | undefined,
  canEdit: boolean,
): boolean {
  if (!canEdit || !row) return false;
  if (quotationIsViewOnly(row)) return false;
  const s = row.progress_status ?? 'draft';
  return (
    s === 'draft' ||
    quotationAllowsPicRevisionEdit(row) ||
    quotationIsReturnedToPicStatus(s)
  );
}

/**
 * PIC menyetujui quotation yang baru diubah (mis. titik sampling bertambah)
 * lalu meneruskan ke SPV (`submitted`).
 */
export function canShowPicApproveRevision(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsViewOnly(row)) return false;
  if (!user) return false;
  const isRevision = quotationAllowsPicRevisionEdit(row);
  if (!isRevision) return false;
  if (isAdminLhu(user)) return true;
  if (isMarketingPic(user)) {
    if (!row.assignee_user) return true;
    return isQuotationAssignee(row, user);
  }
  return false;
}

export function canShowSpvApprove(row: StrapiQuotationRow, user: StrapiUser | null | undefined): boolean {
  if (quotationIsViewOnly(row)) return false;
  return canUseSpvWorkflowActions(row, user) && row.progress_status === 'submitted';
}

export function canShowSpvReject(row: StrapiQuotationRow, user: StrapiUser | null | undefined): boolean {
  if (quotationIsViewOnly(row)) return false;
  return canUseSpvWorkflowActions(row, user) && row.progress_status === 'submitted';
}

/** PIC marketing menandai persetujuan pelanggan (penanggung jawab atau user tipe PIC). */
function canUsePicCustomerApproveActions(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (isAdminLhu(user)) return true;
  if (!isMarketingPic(user)) return false;
  if (!row.assignee_user) return true;
  return isQuotationAssignee(row, user);
}

function quotationManajerAlreadyApproved(row: StrapiQuotationRow): boolean {
  return Boolean(row.manajer_approved_date?.trim());
}

/** Status quotation saat tombol "Pelanggan setuju" tampil untuk PIC, terlepas dari role pemirsa. */
export function quotationAwaitingCustomerApprove(
  row: StrapiQuotationRow,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  if (quotationIsViewOnly(row)) return false;
  const s = row.progress_status ?? '';
  // Hanya sebelum pelanggan setuju; `manajer_approved_date` tetap ada setelah status berubah.
  if (s !== 'approved' && s !== 'manajer_approved') return false;
  if (s === 'manajer_approved') return true;
  if (quotationManajerAlreadyApproved(row)) return true;
  if (opts?.grandTotal == null || !Number.isFinite(opts.grandTotal)) {
    // Grid/list belum punya grand total — mount tombol; validasi ulang setelah detail dimuat.
    return true;
  }
  return !quotationRequiresManajerApproval(opts.grandTotal);
}

export function canShowPicCustomerApprove(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  if (!canUsePicCustomerApproveActions(row, user)) return false;
  return quotationAwaitingCustomerApprove(row, opts);
}

/** @deprecated gunakan {@link canShowPicCustomerApprove} */
export function canShowSpvCustomerApprove(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  return canShowPicCustomerApprove(row, user, opts);
}

/** Manajer marketing setujui quotation ≥ Rp 20 juta (setelah SPV). */
export function canShowManajerApprove(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  if (quotationIsViewOnly(row)) return false;
  if (!isMarketingManajer(user) && !isAdminLhu(user)) return false;
  const s = row.progress_status ?? '';
  if (s === 'need_manajer_approval') return true;
  // Recovery: SPV setujui dari grid tanpa baris sampel → status `approved` padahal ≥ Rp 20 juta.
  if (s === 'approved') {
    if (opts?.grandTotal == null || !Number.isFinite(opts.grandTotal)) {
      return true;
    }
    return quotationRequiresManajerApproval(opts.grandTotal);
  }
  return false;
}

/** Manajer marketing tolak quotation ≥ Rp 20 juta (kembalikan ke PIC). */
export function canShowManajerReject(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsViewOnly(row)) return false;
  if (!isMarketingManajer(user) && !isAdminLhu(user)) return false;
  return row.progress_status === 'need_manajer_approval';
}

/** Minta revisi — hanya PIC marketing penanggung jawab; semua status kecuali draft & submitted. */
export function canShowEditRequest(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsLocked(row)) return false;
  const s = row.progress_status ?? '';
  if (s === 'draft' || s === 'submitted' || s === '') return false;
  if (!user) return false;
  if (!(isMarketingPic(user) && isQuotationAssignee(row, user))) return false;
  const ers = quotationEditRequestStatus(row);
  // Sudah ada permintaan menunggu / disetujui — jangan minta lagi.
  if (ers === 'pending' || ers === 'approved') return false;
  return true;
}

/** SPV menyetujui permintaan revisi PIC (`edit_request_status` → approved). */
export function canShowEditRequestApprove(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsLocked(row)) return false;
  if (!canUseSpvWorkflowActions(row, user)) return false;
  return quotationEditRequestStatus(row) === 'pending';
}

/** SPV menolak permintaan revisi PIC (`edit_request_status` → rejected; status utama tetap). */
export function canShowEditRequestReject(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (quotationIsLocked(row)) return false;
  if (!canUseSpvWorkflowActions(row, user)) return false;
  return quotationEditRequestStatus(row) === 'pending';
}

/** @deprecated gunakan {@link canShowEditRequest} */
export function canShowSpvEditRequest(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  return canShowEditRequest(row, user);
}

export function canShowAnySpvWorkflowAction(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  return (
    canShowSpvApprove(row, user) ||
    canShowSpvReject(row, user) ||
    canShowEditRequestApprove(row, user) ||
    canShowEditRequestReject(row, user)
  );
}

export function canShowAnyPicWorkflowAction(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  return (
    canShowPicCustomerApprove(row, user, opts) ||
    canShowPicApproveRevision(row, user) ||
    canShowEditRequest(row, user)
  );
}

export function canShowAnyManajerWorkflowAction(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  return canShowManajerApprove(row, user, opts) || canShowManajerReject(row, user);
}

/**
 * Tombol buat work order manual disembunyikan.
 * Metode diantar membuat work order otomatis saat pelanggan setuju.
 */
export function canShowCreateWorkOrder(
  _row: StrapiQuotationRow,
  _user: StrapiUser | null | undefined,
): boolean {
  return false;
}

/** Tahapan penjadwalan sampling di grid quotation. */
export function quotationAllPlansHaveWorkOrder(row: StrapiQuotationRow): boolean {
  const plans = row.sampling_plans ?? [];
  if (plans.length === 0) return false;
  return plans.every((plan) => Boolean(plan.work_order?.documentId?.trim()));
}

/** Tahapan penjadwalan sampling di grid quotation. */
export function resolveQuotationTahapan(row: StrapiQuotationRow): string {
  const usesGroups = quotationUsesSampleGroups(row);
  const schedules = row.sampling_schedules ?? [];

  if (!usesGroups && row.work_order_created) return 'Work order telah dibuat';
  if (usesGroups && quotationAllPlansHaveWorkOrder(row)) return 'Work order telah dibuat';

  let hasInProgress = false;
  let hasApproved = false;
  let hasWoCreatedSchedule = false;

  for (const schedule of schedules) {
    const status = String(schedule.progress_status ?? '').trim();
    if (status === 'work_order_created') hasWoCreatedSchedule = true;
    else if (status === 'approved') hasApproved = true;
    else if (status === 'draft' || status === 'scheduling_in_progress') hasInProgress = true;
  }

  if (hasInProgress) return 'Sedang dijadwalkan';
  if (hasApproved) return 'Terjadwal';
  if (!usesGroups && hasWoCreatedSchedule) return 'Work order telah dibuat';
  if (usesGroups && (hasWoCreatedSchedule || row.work_order_created)) return 'Sedang dijadwalkan';

  return '—';
}

function canAccessSamplingScheduleMenu(accessCodes?: readonly string[] | null): boolean {
  if (isSuperadminUser(accessCodes)) return true;
  return (
    accessCodes?.includes('can_view_sampling_schedule') === true ||
    accessCodes?.includes('can_view_pre_survey') === true
  );
}

/** Boleh buka halaman penjadwalan sampling (menu atau peran workflow marketing/admin). */
export function canAccessSamplingSchedulesPage(
  user: StrapiUser | null | undefined,
  accessCodes?: readonly string[] | null,
): boolean {
  if (!user) return false;
  if (canAccessSamplingScheduleMenu(accessCodes)) return true;
  return (
    isAdminSampling(user) ||
    isAdminLhu(user) ||
    isAdminWorkOrderPic(user) ||
    isMarketingPic(user) ||
    isMarketingSpv(user) ||
    isMarketingManajer(user)
  );
}

function workOrderJobIssued(workOrder?: { job_number?: string | null } | null): boolean {
  return Boolean(String(workOrder?.job_number ?? '').trim());
}

/**
 * Penjadwalan ditutup hanya setelah setiap unit punya nomor job.
 * Quotation biasa: satu work order. Konsultan: tiap quotation group.
 * Kontrak: tiap quotation group + recurring event.
 */
export function quotationSamplingJobFullyIssued(row: StrapiQuotationRow): boolean {
  if (row.is_contract) {
    const events = (row.quotation_groups ?? []).flatMap(
      (group) => group.quotation_recurring_events ?? [],
    );
    if (!events.length) return false;
    return events.every((event) => workOrderJobIssued(event.sampling_schedule?.work_order));
  }

  if (quotationUsesSampleGroups(row)) {
    const plans = row.sampling_plans ?? [];
    const groupIds = [
      ...new Set(
        plans
          .map((plan) => plan.quotation_group?.documentId?.trim() ?? '')
          .filter(Boolean),
      ),
    ];
    if (!groupIds.length) return false;
    return groupIds.every((groupId) =>
      plans.some(
        (plan) =>
          plan.quotation_group?.documentId?.trim() === groupId &&
          workOrderJobIssued(plan.work_order),
      ),
    );
  }

  if ((row.sampling_schedules ?? []).some((schedule) => workOrderJobIssued(schedule.work_order))) {
    return true;
  }
  return (row.sampling_plans ?? []).some((plan) => workOrderJobIssued(plan.work_order));
}

/** Link ke penjadwalan setelah customer approved — admin work order dan admin sampling. */
export function canShowViewSamplingSchedules(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  _accessCodes?: readonly string[] | null,
): boolean {
  if (!user) return false;
  if (row.progress_status !== 'customer_approved') return false;
  if (row.sample_pickup_method === 'diantar') return false;
  if (!isAdminWorkOrderPic(user) && !isAdminSampling(user)) return false;
  if (isConsultantOnlyQuotation(row) && consultantQuotationLhuFullyIssued(row)) return false;
  return true;
}

/** Ada tombol workflow di footer halaman view (selain Tutup / Ubah). */
export function canShowQuotationViewWorkflowFooter(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  accessCodes?: readonly string[] | null,
): boolean {
  return (
    canShowSpvApprove(row, user) ||
    canShowSpvReject(row, user) ||
    canShowPicCustomerApprove(row, user) ||
    canShowPicApproveRevision(row, user) ||
    canShowManajerApprove(row, user) ||
    canShowManajerReject(row, user) ||
    canShowEditRequest(row, user) ||
    canShowEditRequestApprove(row, user) ||
    canShowEditRequestReject(row, user) ||
    canShowCreateWorkOrder(row, user) ||
    canShowViewSamplingSchedules(row, user, accessCodes)
  );
}

/** Quotation sudah pernah / sedang setelah persetujuan SPV (tombol preview PDF). */
export function quotationHasSpvApproval(row: {
  progress_status?: string | null;
  spv_approved_date?: string | null;
}): boolean {
  const s = (row.progress_status ?? '').trim();
  if (s === 'need_manajer_approval' || s === 'submitted' || s === 'need_attention') return false;
  if (row.spv_approved_date?.trim() && s !== 'need_manajer_approval') return true;
  return (
    s === 'approved' ||
    s === 'manajer_approved' ||
    s === 'customer_approved' ||
    s === 'edit_requested' ||
    s === 'in_progress' ||
    s === 'completed' ||
    s === 'paid' ||
    s === 'cancelled'
  );
}

export function canShowQuotationPdfPreview(row: StrapiQuotationRow): boolean {
  return quotationHasSpvApproval(row);
}

/** Grid: aksi workflow SPV/manajer, penjadwalan sampling (admin work order), dll. */
export function canShowQuotationWorkflowUi(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
  accessCodes?: readonly string[] | null,
): boolean {
  return (
    canShowAnySpvWorkflowAction(row, user) ||
    canShowAnyManajerWorkflowAction(row, user) ||
    canShowAnyPicWorkflowAction(row, user) ||
    canShowCreateWorkOrder(row, user) ||
    canShowViewSamplingSchedules(row, user, accessCodes)
  );
}

export function canShowEditInGrid(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  return canPicEditQuotation(row, user);
}

/** Copy quotation hanya untuk PIC marketing; hasil copy selalu draft baru. */
export function canCopyQuotation(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (!row.documentId?.trim()) return false;
  return isMarketingPic(user);
}

/** Filter daftar: PIC melihat quotation sendiri; SPV melihat yang sudah diajukan ke atas. */
export function defaultListStatusFilterForUser(
  user: StrapiUser | null | undefined,
): string {
  if (isMarketingSpv(user)) return '';
  return '';
}

export function quotationVisibleInListForUser(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (!user) return false;
  if (isAdminLhu(user)) return true;
  if (isMarketingSpv(user)) {
    const s = row.progress_status ?? '';
    return s !== 'draft' && s !== '';
  }
  if (isMarketingManajer(user)) {
    const s = row.progress_status ?? '';
    return (
      s === 'need_manajer_approval' ||
      s === 'approved' ||
      s === 'manajer_approved' ||
      s === 'customer_approved'
    );
  }
  if (isMarketingPic(user)) {
    const s = row.progress_status ?? '';
    return (
      isQuotationAssignee(row, user) ||
      s === 'draft' ||
      s === 'rejected' ||
      s === 'rejected_by_manajer' ||
      s === 'need_attention' ||
      s === 'edit_requested' ||
      s === 'need_manajer_approval' ||
      s === 'approved' ||
      s === 'manajer_approved' ||
      s === 'customer_approved'
    );
  }
  return true;
}
